/**
 * DateChipPicker Component
 * Inline chip/icon trigger for the date suggestion popover.
 * Shows a calendar icon when no date is selected, or a chip with the date label + clear button.
 */

import { useState } from 'react';
import { Calendar, X } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { isToday, isTomorrow } from '@/lib/calendarUtils';
import { cn } from '@/lib/utils';
import { formatTime12h } from './dateSuggestions';
import { DateSuggestionPopover } from './DateSuggestionPopover';

// ============================================================================
// Types
// ============================================================================

export interface DateChipPickerProps {
  /** Selected date in YYYY-MM-DD format */
  selectedDate?: string;
  /** Selected time in HH:mm format */
  selectedTime?: string;
  /** Called when a date is selected from the popover */
  onSelect: (date?: string, time?: string) => void;
  /** Called when the date chip is cleared */
  onClear: () => void;
}

// ============================================================================
// Helpers
// ============================================================================

function getChipLabel(dateStr: string, timeStr?: string): string {
  const date = parseISO(dateStr);
  let label: string;

  if (isToday(date)) {
    label = 'Today';
  } else if (isTomorrow(date)) {
    label = 'Tom';
  } else {
    label = format(date, 'EEE, MMM d');
  }

  if (timeStr) {
    label += ` ${formatTime12h(timeStr)}`;
  }

  return label;
}

// ============================================================================
// Component
// ============================================================================

export function DateChipPicker({
  selectedDate,
  selectedTime,
  onSelect,
  onClear,
}: DateChipPickerProps) {
  const [popoverOpen, setPopoverOpen] = useState(false);

  function handleClear(e: React.MouseEvent): void {
    e.stopPropagation();
    onClear();
  }

  // No date selected: show calendar icon trigger
  if (!selectedDate) {
    return (
      <DateSuggestionPopover
        open={popoverOpen}
        onOpenChange={setPopoverOpen}
        onSelect={onSelect}
      >
        <button
          type="button"
          className={cn(
            'flex items-center justify-center p-1 rounded-sm',
            'text-muted-foreground hover:text-foreground transition-colors'
          )}
          aria-label="Set due date"
        >
          <Calendar className="h-4 w-4" />
        </button>
      </DateSuggestionPopover>
    );
  }

  // Date selected: show chip with label + clear button
  const chipLabel = getChipLabel(selectedDate, selectedTime);

  return (
    <DateSuggestionPopover
      open={popoverOpen}
      onOpenChange={setPopoverOpen}
      onSelect={onSelect}
    >
      <button
        type="button"
        className={cn(
          'inline-flex items-center gap-1',
          'bg-primary/10 text-primary text-xs font-medium',
          'px-2 py-0.5 rounded-full',
          'max-w-[120px] hover:bg-primary/15 transition-colors'
        )}
        aria-label="Change due date"
      >
        <span className="truncate">{chipLabel}</span>
        <X
          className="h-3 w-3 shrink-0 opacity-60 hover:opacity-100"
          onClick={handleClear}
          aria-label="Clear due date"
        />
      </button>
    </DateSuggestionPopover>
  );
}
